import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, useEffect } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import AcquisitionsAPI from "@/api/acquisitions/index.js";
import { DropDownItem, DropDownMenu } from "@monolith-forensics/monolith-ui";
import {
  InfiniteQueryData,
  Acquisition,
  AcquisitionPage,
} from "../acquisitions.types";

interface StatusItem {
  status: string;
  status_id: number;
  color: string;
}

interface RowData {
  acq_id: number;
  status: string;
}

interface StatusSelectorProps {
  rowData: RowData;
  queryKey?: string[] | string;
  onChange?: (selectedStatus: StatusItem) => void;
}

const StatusSelector = ({
  rowData,
  queryKey,
  onChange = () => {},
}: StatusSelectorProps) => {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData(["acquisitions:status"]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Local state to track current status for immediate UI updates
  const [currentStatusValue, setCurrentStatusValue] = useState(rowData?.status);

  const { data: statusItems } = useQuery<StatusItem[]>({
    queryKey: ["acquisitions:status"],
    queryFn: () => AcquisitionsAPI.getStatuses(),
    enabled: !cachedData,
    placeholderData: () => (cachedData as StatusItem[]) || [],
  });

  // Update local state when rowData changes (from cache updates)
  useEffect(() => {
    setCurrentStatusValue(rowData?.status);
  }, [rowData?.status]);

  const currentStatus = statusItems?.find(
    (p: StatusItem) => p.status === currentStatusValue
  );

  const handleItemSelect = async (item: DropDownItem) => {
    if (containerRef.current) {
      containerRef.current.classList.remove("custom-action-button");
    }

    // Find the full status object from statusItems using the selected item's value (status_id)
    const selectedStatus = statusItems?.find(
      (p: StatusItem) => p.status_id === Number(item.value)
    );

    if (!selectedStatus) return;

    // Immediately update local state for UI responsiveness
    setCurrentStatusValue(selectedStatus.status);

    try {
      // Make the API call to update the status
      await AcquisitionsAPI.updateAcquisition(rowData.acq_id, {
        status: selectedStatus.status,
      });

      // Call the onChange callback
      onChange(selectedStatus);

      // Invalidate queries to refresh the data from the server
      // This is more robust than trying to manually update the cache
      queryClient.invalidateQueries({
        queryKey: ["acquisitions:list"],
      });
    } catch (error) {
      console.error("Failed to update acquisition status:", error);
      // Revert the local state if the API call fails
      setCurrentStatusValue(rowData?.status);
    }
  };

  const dropdownItems =
    statusItems?.map((p: StatusItem) => ({
      label: p.status,
      value: p.status_id.toString(),
    })) || [];

  return (
    <div
      ref={containerRef}
      className={`${currentStatus ? "" : "custom-action-button"}`}
    >
      <DropDownMenu
        data={dropdownItems}
        disabled={!hasPermission()}
        variant="contained"
        onItemSelect={handleItemSelect}
        buttonProps={{
          title: "Select Status",
          style: {
            fontSize: 10,
            color: currentStatus?.color,
            backgroundColor: currentStatus?.color + "25",
            fontWeight: 500,
            padding: "4px 8px",
            minHeight: "24px",
            height: "24px",
          },
        }}
      >
        {currentStatusValue ? (
          <div style={{ fontSize: 10 }}>{currentStatusValue}</div>
        ) : (
          <div style={{ fontSize: 10 }}>Select...</div>
        )}
      </DropDownMenu>
    </div>
  );
};

export default StatusSelector;
